import { Dropdown, Menu } from "antd";
import moment from "moment";
import {
    type ReactElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Route, useLocation } from "react-router";

import {
    IconCaretSmallDown,
    IconCaretSmallRight,
    IconCaretSmallUp,
    IconExclamationCircle,
    IconHelp,
    IconList,
    IconLiveChat,
    IconMagnifyingGlass,
    IconMegaphone,
    IconNotifications,
    IconPlus,
    IconUsers,
    IconX,
} from "@btnet/blueprint-icons";
import { BTButton } from "@btnet/core-components/BTButton";
import { BTDrawer } from "@btnet/core-components/BTDrawer";
import { BTNativeBackCloseButton } from "@btnet/core-components/BTNativeBackCloseButton";
import {
    MainNavigationStickyHeaderHeight,
    useStickyHeight,
} from "@btnet/core-components/hooks/StickyContext";
import { useResizeObserver } from "@btnet/core-components/hooks/useResizeObserver";
import { cn } from "@btnet/utilities-core";

import { type BuilderInfo, type UserInfo } from "helpers/AppProvider.types";
import { AppDefaultInfoContext } from "helpers/globalContext/AppDefaultInfoContext";
import { BuilderInfoContext, WhenBuilderFlag } from "helpers/globalContext/BuilderInfoContext";
import { EnvironmentInfoContext } from "helpers/globalContext/EnvironmentInfoContext";
import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";
import { queryClient } from "helpers/queryClient";

import { BTLocalStorage, LocalStorageKeys } from "types/btStorage";
import { BTLoginTypes, MenuItemName, SummaryGridActionQueryParamTypes } from "types/enum";

import { track, useTracking } from "utilities/analytics/analytics";
import { showAPIErrorMessage } from "utilities/apiHandler";
import { jobDetailRoute } from "utilities/flatRoutes/job.routes";
import { type IRouteLocationState } from "utilities/flatRoutes/usePreviousLocation/usePreviousLocation.types";
import { lazyLoadComponent } from "utilities/LazyLoad.utility";
import { routes } from "utilities/routes";
import { formattedPlural } from "utilities/string/string";
import { reloadQualtrics } from "utilities/thirdPartyIntegrations/qualtrics/qualtrics";
import { useRouteProps } from "utilities/url/url";

import { BTAlert } from "commonComponents/btWrappers/BTAlert/BTAlert";
import { BTLink, BTLinkRelative } from "commonComponents/btWrappers/BTLink/BTLink";
import { BTLogo } from "commonComponents/btWrappers/BTLogo/BTLogo";
import { BTUser } from "commonComponents/btWrappers/BTUser/BTUser";
import { RouteJob } from "commonComponents/entity/job/RouteJob/RouteJob";
import { ClientObservableBackgroundJobStatusPopup } from "commonComponents/utilities/ClientObservableBackgroundJobStatusPopup/ClientObservableBackgroundJobStatusPopup";
import { GetEphDaysRemaining } from "commonComponents/utilities/MainNavigation/common/EphInfo/EphInfo";
import { MainNavLink } from "commonComponents/utilities/MainNavigation/common/MainNavLink";
import { GlobalSearch } from "commonComponents/utilities/MainNavigation/globalSearch/GlobalSearch";
import { InfoArchOptInToggle } from "commonComponents/utilities/MainNavigation/InfoArchOptInToggle/InfoArchOptInToggle";
import {
    MainNavBadge,
    NewNotificationsBadge,
} from "commonComponents/utilities/MainNavigation/MainNavActionItems/MainNavActionItems";
import {
    ChatReadQueryKey,
    MainNavigationHandler,
    useGetChatQuery,
} from "commonComponents/utilities/MainNavigation/MainNavigation.api.handler";
import {
    MainNavigationResponse,
    type NavMenuItemResponse,
} from "commonComponents/utilities/MainNavigation/MainNavigation.api.types";
import {
    getQuickAddActions,
    getQuickAddMenuOptions,
    type IBTSubMenuItemProps,
    type IMainNavChildNode,
    type IMainNavHomeButtonProps,
    type IMainNavigationProps,
    type IMainNavMenusProps,
    type IMainNavNode,
    type IMainNavTitleProps,
    type IMainNavVerticalLocalProps,
    type IMenuItemClickRequest,
    type IMenuItemsProps,
    type IMenuTabInfo,
    type IMenuTabInfoQuickAdd,
    type INotificationUnfollowModel,
    isIMenuTabInfo,
    type ITemplateSelectionsState,
    MAIN_NAVIGATION_BREAKPOINT,
    MAIN_NAVIGATION_TOP_LEVEL_ACTIONS,
    MAIN_NAVIGATION_TOP_LEVEL_MENUS,
    MenuInfo,
    menuItemClass,
    MenuOnClickTypes,
    type TMainNavMenuKeys,
} from "commonComponents/utilities/MainNavigation/MainNavigation.types";
import { SearchDialogTrigger } from "commonComponents/utilities/MainNavigation/SearchDialog/SearchDialogTrigger";
import { useTemplateSelectionsState } from "commonComponents/utilities/MainNavigation/useTemplateSelectionsState";
import { RouteRelative } from "commonComponents/utilities/RouteRelative/RouteRelative";
import { ShowOnPortal } from "commonComponents/utilities/ShowOnPortal/ShowOnPortal";

import { RouteBidPackage } from "entity/bidPackage/BidPackage/BidPackageRoute";
import { RouteBill } from "entity/bill/Bill/RouteBill";
import { RouteChangeOrderQuickAdd } from "entity/changeOrder/ChangeOrder/ChangeOrderRoute";
import { RouteContact } from "entity/contact/Contact/RouteContact";
import { RouteContactUs } from "entity/ContactUs/RouteContactUs";
import { RouteInternalUser } from "entity/internalUser/InternalUserDetails/RouteInternalUser";
import RouteMyUserDetails from "entity/internalUser/InternalUserDetails/RouteMyUserDetails";
import { RouteInternalUserRoleDetails } from "entity/internalUser/InternalUserRoleDetails/RouteInternalUserRoleDetails";
import { RouteInternalUserRolesList } from "entity/internalUser/InternalUserRoleSettings/InternalUserRoleList/RouteInternalUserRolesList";
import { JobDetailTab } from "entity/job/Job.api.types";
import { getLeadTabTypeFromUrl, LeadTabTypes } from "entity/lead/Common/LeadTab/LeadTab.types";
import { RouteLead } from "entity/lead/Lead/LeadRoute";
import { RouteLeadActivity } from "entity/lead/LeadActivity/LeadActivityRoute";
import { RouteMessageCompose } from "entity/message/MessageCompose/MessageComposeRoute";
import NotificationPanel from "entity/notification/panel/NotificationPanel";
import { NotificationPanelRoutes } from "entity/notification/panel/NotificationPanelRoutes";
import { RouteOwnerInvoice } from "entity/ownerInvoice/OwnerInvoice/OwnerInvoiceRoute";
import { RouteOwnerSetup } from "entity/OwnerSetup/RouteOwnerSetup";
import { RouteClientPortalSettingsDrawer } from "entity/OwnerSummary/ClientPortalSettingsDrawer/RouteClientPortalSettingsDrawer";
import { RoutePurchaseOrder } from "entity/purchaseOrder/PurchaseOrder/PurchaseOrderRoute";
import { RouteUploadReceipt } from "entity/receipt/ReceiptUpload/RouteUploadReceipt";
import { RouteRfi } from "entity/rfi/Rfi/RfiRoute";
import { RouteSchedule } from "entity/schedule/Schedule/RouteSchedule";
import { RouteSelection } from "entity/selection/Selection/RouteSelection";
import { RouteSelectionQuickAdd } from "entity/selection/SelectionQuickAdd/SelectionQuickAdd";
import { RouteSelectionOption } from "entity/selectionOption/SelectionOptionRoute";
import { builderSupportsTemplateSelectionOptions } from "entity/selectionOption/selectionOptionsUtilities";
import { RouteSpecification } from "entity/specification/SpecificationRoute";
import { RouteSubSetup } from "entity/sub/SubSetup/RouteSubSetup";
import { RouteSubVendor } from "entity/sub/SubVendor/RouteSubVendor";
import { RouteSubscriptionManager } from "entity/SubscriptionManager/SubscriptionManagerRoute";
import { RouteTemplate } from "entity/template/Template/RouteTemplate";
import { RouteTemplateFromTemplate } from "entity/template/TemplateFromTemplate/RouteTemplateFromTemplate";
import { RouteShift } from "entity/timeClock/Shift/RouteShift";
import { RouteToDo } from "entity/toDo/details/ToDoDetails/RouteToDo";
import { RouteWarranty } from "entity/warranty/Warranty/RouteWarranty";
import { RouteWarrantyAddQuickAction } from "entity/warranty/Warranty/RouteWarrantyAddQuickAction";
import { WhatsNewPanel } from "entity/whatsNew/panel/WhatsNewPanel";
import { WhatsNewNavBadge } from "entity/whatsNew/WhatsNewNavBadge";

import "./MainNavigation.less";

const NotificationUnfollow = lazyLoadComponent(
    () => import("entity/notification/NotificationUnfollow/NotificationUnfollow"),
);

const QuickBooksSyncSchedule = lazyLoadComponent(
    () =>
        import(
            "entity/accounting/AccountingSettings/QuickBooksSyncSchedule/QuickBooksSyncSchedule"
        ),
);

const defaultMainNavigationHandler = new MainNavigationHandler();
const defaultMenuInfo = new MenuInfo();
// for replacing right nav titles with icons only (Users, Help, Settings)
function getOverriddenIcon(title: string, userInfoContext: UserInfo): ReactElement | undefined {
    switch (title) {
        case "Users":
            return <IconUsers />;
        case "Help":
            return <IconHelp />;
        case "Settings":
            return (
                <BTUser
                    size="small"
                    displayName={userInfoContext?.fullName || ""}
                    globalUserId={userInfoContext?.globalUserId || -1}
                    contactCard="none"
                />
            );
        default:
            return undefined;
    }
}

function shouldDisplayMenuChild(
    categoryData: MainNavigationResponse[keyof MainNavigationResponse],
    key: string,
    infoObj: Record<string, IMenuTabInfo | string>,
    isTemplateMode: boolean,
    builderInfo: BuilderInfo,
    loginType: BTLoginTypes,
    templateSelectionsState: ITemplateSelectionsState,
) {
    const hasTabInfo = infoObj[key] !== undefined && key !== "title";
    if (!hasTabInfo) return false;
    const navItem = categoryData[key as keyof typeof categoryData] as NavMenuItemResponse;
    const infoItem = infoObj[key];
    if (!isIMenuTabInfo(infoItem)) return false; // only render fields which are type IMenuTabInfo

    const isVisible = navItem?.visible ?? false;
    const showInTemplate = isTemplateMode
        ? (infoItem.showInTemplateMode?.(builderInfo, templateSelectionsState) ?? false)
        : true;
    const showOnPortal = infoItem.showOnPortal
        ? (infoItem.showOnPortal.includes(loginType) ?? false)
        : true;

    return isVisible && showInTemplate && showOnPortal;
}
// Helper function to parse a hex color string into RGB.
const hexToRgb = (hex: string) => {
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF").
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? {
              r: parseInt(result[1], 16),
              g: parseInt(result[2], 16),
              b: parseInt(result[3], 16),
          }
        : null;
};

// Function to convert an sRGB color channel to a linear value.
const sRGBtoLin = (channel: number) => {
    channel /= 255;
    return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
};

/**
 * Determines if a hex color is "dark" based on WCAG perceived luminance.
 * @param hex A hex color string, e.g., '#FFFFFF' or '000'.
 * @param threshold A luminance threshold between 0 and 1. Defaults to 0.5.
 * @returns `true` if the color is dark, otherwise `false`.
 */
export const isDark = (hex: string, threshold = 0.5): boolean => {
    const rgb = hexToRgb(hex);
    if (!rgb) {
        return false;
    }

    // Calculate the relative luminance.
    const rLin = sRGBtoLin(rgb.r);
    const gLin = sRGBtoLin(rgb.g);
    const bLin = sRGBtoLin(rgb.b);

    const luminance = 0.2126 * rLin + 0.7152 * gLin + 0.0722 * bLin;
    return luminance < threshold;
};
export interface IMainNavChildNodeWithSortingData extends IMainNavChildNode {
    // extra fields for sorting
    isQuickAddPreference: boolean;
    quickAddLink: IRouteLocationState | string;
    clickCount: number;
}

function isChildNodeWithQuickAddLink(node: IMainNavNode): node is IMainNavChildNodeWithSortingData {
    return "quickAddLink" in node;
}

const MAX_QUICK_ADD_DISPLAY_COUNT = 5;

/**
 * Increments the click count for a quick add item in local storage.
 */
export function trackQuickAddClick(itemKey: string): void {
    const clickCounts = BTLocalStorage.get("bt-object-quickAddClickCounts");
    const currentCount = clickCounts[itemKey] ?? 0;
    BTLocalStorage.set("bt-object-quickAddClickCounts", {
        ...clickCounts,
        [itemKey]: currentCount + 1,
    });
}

/**
 * Sorts quick add items based on click counts stored in local storage.
 * Items with higher counts appear first.
 * Items with equal counts (or both 0) are sorted alphabetically by title.
 */
function sortQuickaddItemsByClickCount(
    items: IMainNavChildNodeWithSortingData[],
): IMainNavChildNodeWithSortingData[] {
    return [...items].sort((a, b) => {
        const aCount = a.clickCount;
        const bCount = b.clickCount;

        // Sort by count descending (higher counts first)
        if (aCount !== bCount) {
            return bCount - aCount;
        }
        // Equal counts - sort alphabetically by title
        return a.title.localeCompare(b.title);
    });
}

function calculateQuickAddPermission(
    itemResponse: NavMenuItemResponse,
    menuTabInfo: IMenuTabInfo,
    data: MainNavigationResponse,
): { hasQuickAddPermission: boolean; quickAddActions: IMenuTabInfoQuickAdd[] } {
    const quickAddActions = getQuickAddActions(itemResponse, menuTabInfo, data);
    return {
        hasQuickAddPermission: quickAddActions.length !== 0,
        quickAddActions,
    };
}

export function buildQuickaddNode(
    menuInfo: MenuInfo,
    data: MainNavigationResponse,
    routeInfo: IMenuItemClickRequest,
    templateSelectionsState?: ITemplateSelectionsState,
) {
    const clickCounts = BTLocalStorage.get("bt-object-quickAddClickCounts");

    const allQuickAddItems: IMainNavChildNodeWithSortingData[] = [];

    const quickAddMenuOptions = getQuickAddMenuOptions({
        menuInfo,
        builderInfo: routeInfo.builderInfo,
        isTemplateMode: routeInfo.isTemplateMode,
        templateSelectionsState,
    });

    quickAddMenuOptions.forEach((option) => {
        const { parentKey, itemKey, title } = option;
        // data from the backend, contains visibility, popularity of item for quick add, etc
        const menuResponseData = data[parentKey];
        // constant data from the frontend about title, icons, routing
        const specificMenuInfo = menuInfo[parentKey];
        const itemResponse = menuResponseData[itemKey] as NavMenuItemResponse;
        const menuTabInfo = specificMenuInfo[itemKey] as IMenuTabInfo;
        if (!itemResponse || !menuTabInfo) return;

        const { isQuickAddPreference, canQuickAdd, visible } = itemResponse;
        const { hasQuickAddPermission, quickAddActions } = calculateQuickAddPermission(
            itemResponse,
            menuTabInfo,
            data,
        );
        // follow pre-existing logic
        let quickAddLink = undefined;
        if (hasQuickAddPermission) {
            quickAddLink = quickAddActions[0].quickAddReactRoute(routeInfo);
        }

        if (canQuickAdd && visible) {
            const nodeData = {
                key: itemKey,
                title,
                testId: menuTabInfo.testid,
                icon: menuTabInfo.icon,
                info: menuTabInfo,
                navItem: itemResponse,
                disabled: false,
                // add extra fields for sorting
                isQuickAddPreference,
                quickAddLink,
                clickCount: clickCounts[itemKey] ?? 0,
            } as IMainNavChildNodeWithSortingData;
            allQuickAddItems.push(nodeData);
        }
    });

    // Sort items based on click counts from local storage
    const sortedItems = sortQuickaddItemsByClickCount(allQuickAddItems);

    return {
        key: "quickadd",
        title: "Quick Add",
        testId: "exp-personalized-main-nav-button",
        icon: <IconPlus />,
        children: sortedItems,
        disabled: false,
        clickCounts,
    } as IMainNavNode<IMainNavChildNodeWithSortingData> & { clickCounts: Record<string, number> };
}

/**
 * @description reduces MenuInfo, which represents constant menu structure, and MainNavigationResponse, which contains menu item visibility, into data for presenting a Submenu
 * @param menuInfo constant menu structure and behavior info
 * @param data menu item visibility and titles from API
 * @param isTemplateMode indicates if template mode is active, which changes some titles and visibility of items
 * @param loginType type of login for visibility control
 * @param topLevelKeys used for picking menus from the MenuInfo, by key
 * @param builderInfo BuilderInfo context must be passed in to avoid requiring this function to be a hook
 * @param userInfo UserInfo context must be passed in to avoid requiring this function to be a hook
 * @returns array of navigation nodes to be rendered in a Menu.Submenu
 */
export function buildNavNodes(
    menuInfo: MenuInfo,
    data: MainNavigationResponse,
    isTemplateMode: boolean,
    loginType: BTLoginTypes,
    topLevelKeys: TMainNavMenuKeys[],
    jobId: number,
    builderInfo: BuilderInfo,
    userInfo: UserInfo,
    templateSelectionsState: ITemplateSelectionsState,
): IMainNavNode[] {
    return topLevelKeys
        .map((categoryKey) =>
            buildNavNodeForCategory(
                menuInfo,
                data,
                isTemplateMode,
                loginType,
                categoryKey,
                jobId,
                builderInfo,
                userInfo,
                templateSelectionsState,
            ),
        )
        .filter((n): n is IMainNavNode => n !== null);
}

/**
 * builds a navigation node for a top-level category, which may contain multiple child nodes (sales, job, project management, etc)
 * @param menuInfo constant menu structure and behavior info
 * @param data menu item visibility and titles from API
 * @param isTemplateMode boolean indicating if template mode is active, which changes some titles and visibility of items
 * @param loginType type of login for visibility control
 * @param categoryKey key of the category to build from MenuInfo
 * @param jobId current job id, used for disabling some menu items
 * @param templateSelectionsState current template's Selections experience, resolved by the caller for the same reason
 * @param builderInfo BuilderInfo context must be passed in to avoid requiring this function to be a hook
 * @param userInfo UserInfo context must be passed in to avoid requiring this function to be a hook
 * @returns navigation node for the category, or null if no children are visible
 */
function buildNavNodeForCategory(
    menuInfo: MenuInfo,
    data: MainNavigationResponse,
    isTemplateMode: boolean,
    loginType: BTLoginTypes,
    categoryKey: TMainNavMenuKeys,
    jobId: number,
    builderInfo: BuilderInfo,
    userInfo: UserInfo,
    templateSelectionsState: ITemplateSelectionsState,
): IMainNavNode | null {
    const categoryData = data[categoryKey];
    if (!categoryData) return null;
    const parentTestId = menuInfo[categoryKey].testid;

    const infoObj = menuInfo[categoryKey] as Record<string, IMenuTabInfo>;
    const orderedKeys = Object.keys(infoObj);

    let childKeys = Object.keys(categoryData)
        .filter((key) =>
            shouldDisplayMenuChild(
                categoryData,
                key,
                infoObj,
                isTemplateMode,
                builderInfo,
                loginType,
                templateSelectionsState,
            ),
        )
        .sort((a, b) => orderedKeys.indexOf(a) - orderedKeys.indexOf(b));

    if (childKeys.length === 0) return null;

    childKeys = reorderChildKeysByUpsell(categoryData, childKeys);

    const parentTitleBase = categoryData.title;
    const parentTitle =
        isTemplateMode && parentTitleBase
            ? parentTitleBase.replace("Job", "Template")
            : parentTitleBase;

    const children: IMainNavChildNode[] = childKeys.map((ck) =>
        buildNavChildNode(ck, infoObj, categoryData, isTemplateMode, jobId, builderInfo, userInfo),
    );

    // reports must be handled differently.  it is currently being refactored by another team so leaving this as is for now
    if (categoryKey === "reports") {
        const only = children[0];
        return {
            key: categoryKey,
            title: only.title,
            testId: parentTestId,
            icon: only.icon,
            info: only.info,
            navItem: only.navItem,
            disabled: only.disabled,
            menuItemName: only.menuItemName,
        } as IMainNavNode;
    }

    return {
        key: categoryKey,
        title: parentTitle,
        testId: parentTestId,
        icon: parentTitle && getOverriddenIcon(parentTitle, userInfo),
        children,
        disabled: false,
    } as IMainNavNode;
}

/**
 * Reorders child keys to place upsell items at the end of the list.
 * @param categoryData main navigation data for the category
 * @param childKeys list of child keys to reorder
 * @returns reordered list of child keys
 */
function reorderChildKeysByUpsell(
    categoryData: MainNavigationResponse[keyof MainNavigationResponse],
    childKeys: string[],
): string[] {
    const [regularItems, upsellItems] = childKeys.reduce(
        ([regular, upsell], key) => {
            // TODO: unclear if this is still used
            const navMenuItem = categoryData[key as keyof typeof categoryData];
            const hasRequiredPackage =
                navMenuItem && typeof navMenuItem === "object" && "requiredPackage" in navMenuItem
                    ? (navMenuItem as NavMenuItemResponse).requiredPackage !== null
                    : false;
            return hasRequiredPackage ? [regular, [...upsell, key]] : [[...regular, key], upsell];
        },
        [[], []] as [string[], string[]],
    );
    return [...regularItems, ...upsellItems];
}

/**
 * builds a navigation child node from the menu info and main navigation response data
 * @param ck child key
 * @param infoObj menu info object for the category
 * @param categoryData main navigation data for the category
 * @param isTemplateMode boolean indicating if template mode is active, which changes some titles and visibility of items
 * @param jobId current job id, used for disabling some menu items
 * @param builderInfo BuilderInfo context must be passed in to avoid requiring this function to be a hook
 * @param userInfo UserInfo context must be passed in to avoid requiring this function to be a hook
 * @returns navigation child node for the menu item
 */
function buildNavChildNode(
    ck: string,
    infoObj: Record<string, IMenuTabInfo>,
    categoryData: MainNavigationResponse[keyof MainNavigationResponse],
    isTemplateMode: boolean,
    jobId: number,
    builderInfo: BuilderInfo,
    userInfo: UserInfo,
): IMainNavChildNode {
    const infoItem = infoObj[ck];
    const navItem = categoryData[ck as keyof typeof categoryData] as NavMenuItemResponse;
    const title = infoItem.getTitle?.(navItem.title, isTemplateMode) ?? navItem.title ?? ck;
    const disabled =
        (infoItem.getIsDisabled?.(userInfo, builderInfo) ||
            (infoItem.enableForSingleJobOnly?.(builderInfo) && jobId <= 0)) ??
        false;
    return {
        key: ck,
        title,
        testId: infoItem.testid,
        icon: infoItem.icon,
        info: infoItem,
        navItem,
        disabled,
        menuItemName: infoItem.menuItemName,
    };
}

const HomeOwnerPreviewBanner = ({
    impersonatingUserCanEditPermissions,
    onEditPermissionsClick,
}: {
    impersonatingUserCanEditPermissions: boolean;
    /**
     * Replaces the default "open the client permissions drawer" behavior. Pass `undefined` to keep
     * the default.
     */
    onEditPermissionsClick: (() => void) | undefined;
}) => {
    const editButton = (
        <BTButton
            data-testid="editClientPermissions"
            type="secondary"
            onClick={onEditPermissionsClick ?? (() => {})}
            id="editClientPermissions"
        >
            Edit permissions
        </BTButton>
    );

    return (
        <BTAlert
            type="warning"
            showIcon
            icon={<IconExclamationCircle size="lg" />}
            data-testid="homeownerPreviewAlert"
            className="HomeownerPreviewAlert"
            message="This is how Buildertrend will look to the client"
            action={
                impersonatingUserCanEditPermissions &&
                (onEditPermissionsClick ? (
                    editButton
                ) : (
                    <BTLinkRelative linkType="menuList" to={routes.job.getClientPortalSettings()}>
                        {editButton}
                    </BTLinkRelative>
                ))
            }
        />
    );
};

const MainNavTitle: React.FC<IMainNavTitleProps> = ({
    title,
    showNavCaret,
    testid,
    closed,
    ariaLabel,
}) => (
    <span
        data-testid={testid}
        style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "space-between" }}
        aria-label={ariaLabel}
    >
        <span className="nav-bar-title">{title}</span>
        {showNavCaret && (
            <IconCaretSmallRight
                className={cn({
                    "nav-bar-title-icon--opened": !closed,
                    "nav-bar-title-icon--closed": closed,
                })}
            />
        )}
    </span>
);
const MainNavHorizontal = ({ children }: { children: React.ReactNode }) => (
    <div className="expanded">{children}</div>
);

const MainNavVertical: React.FC<IMainNavVerticalLocalProps> = ({
    children,
    onOpen,
    loginType,
    backgroundColor,
}) => (
    <>
        <div className="flex flex-row gap-2 items-center">
            <BTButton
                id="responsive-menu-overflow"
                data-testid="responsive-menu-overflow"
                onClick={() => onOpen?.()}
                type="tertiary"
                icon={<IconList />}
                aria-label="Open nav menu"
                className="main-navigation-button"
            />
            <MainNavHomeButton loginType={loginType} backgroundColor={backgroundColor} />
        </div>

        {children}
    </>
);

const MainNavDrawer = ({
    open,
    onClose,
    children,
    loginType,
    backgroundColor,
}: {
    open: boolean;
    onClose: () => void;
    children: React.ReactNode;
    loginType: BTLoginTypes;
    backgroundColor: string;
}) => (
    <BTDrawer
        title={
            <div className="flex flex-row">
                <MainNavHomeButton
                    loginType={loginType}
                    inDrawer
                    backgroundColor={backgroundColor}
                />
                <BTNativeBackCloseButton
                    data-testid="close-drawer-button"
                    aria-label="Close drawer"
                    onClick={onClose}
                />
            </div>
        }
        placement="left"
        onClose={onClose}
        visible={open}
        destroyOnClose
        className="main-navigation-drawer"
        closable={false}
        // Builder-only: this nav is shared with the owner and sub portals, and the Research
        // Preview is a builder-side experience (matches the desktop toggle's gating below).
        footer={
            loginType === BTLoginTypes.BUILDER && (
                <WhenBuilderFlag
                    isEnabled="infoArchOptIn"
                    then={
                        <InfoArchOptInToggle
                            labelClassName="text-fg-default opacity-100"
                            testIdPrefix="mainNavDrawerInfoArchOptIn"
                        />
                    }
                />
            )
        }
    >
        {children}
    </BTDrawer>
);

const MainNavHomeButton: React.FC<IMainNavHomeButtonProps> = ({
    loginType,
    inDrawer,
    backgroundColor,
}) => {
    if (loginType === BTLoginTypes.SUBS) return null;
    const homeRoute = routes.landingPage.getLinkForLoginType(loginType, false);

    const getIconColor = () => {
        if (inDrawer) return "brand";
        if (isDark(backgroundColor)) return "solidInverted";
        return "brand";
    };

    return (
        <BTLink
            id="home"
            data-testid="home-main-nav-button"
            to={homeRoute}
            className="home-nav-link"
        >
            <BTLogo width={18} coloring={getIconColor()} />
        </BTLink>
    );
};

/**
 * The Leads section's sub-tabs (opportunities, activities, calendar, proposals, map) are distinct nav
 * items but every route under it reports the same `MenuItemName.Leads`. Resolve the specific nav item
 * from the current URL's tab segment so the correct sub-tab highlights.
 */
const LEAD_TAB_TO_MENU_ITEM: Partial<Record<LeadTabTypes, MenuItemName>> = {
    [LeadTabTypes.ListView]: MenuItemName.LeadOpportunities,
    [LeadTabTypes.ActivityView]: MenuItemName.LeadActivities,
    [LeadTabTypes.ActivityCalendar]: MenuItemName.LeadActivityCalendar,
    [LeadTabTypes.Proposals]: MenuItemName.LeadProposals,
    [LeadTabTypes.Map]: MenuItemName.LeadMap,
};

/** true if the node represents the given MenuItemName (handles nodes mapped to more than one) */
function nodeMatchesMenuItem(node: IMainNavNode, menuItem: MenuItemName): boolean {
    if (node.menuItemName === undefined) {
        return false;
    }
    return Array.isArray(node.menuItemName)
        ? node.menuItemName.includes(menuItem)
        : node.menuItemName === menuItem;
}

/** identifies the nav item to highlight for the current page */
export interface ISelectedNav {
    /** key of the matched leaf item (the child, or a top-level item like reports) */
    itemKey: string;
    /** key of the matched item's parent category, if it lives inside a submenu */
    parentKey?: string;
}

/**
 * Finds which nav item to highlight for the current page. Walks the built nav tree looking for the
 * node whose `menuItemName` matches the route's `selectedMenuItem`, so highlighting works on any page
 * load (deep link, redirect, refresh) rather than relying on antd's uncontrolled last-click.
 *
 * antd's own `selectedKeys` can't drive this: in horizontal mode the child items live in lazily
 * rendered popups (not in the DOM when collapsed) so their keys never match, and antd does not mark a
 * parent submenu selected from `selectedKeys`. We therefore return the matched item and its parent so
 * the caller can apply the selected class to whichever element is actually rendered (the parent
 * category button in horizontal mode, the child item in inline mode). Returns undefined when nothing
 * should be highlighted (e.g. MenuItemName.None or a page with no nav destination).
 */
export function getSelectedNav(
    navNodes: IMainNavNode[],
    selectedMenuItem: MenuItemName | undefined,
    pathname: string,
): ISelectedNav | undefined {
    if (selectedMenuItem === undefined || selectedMenuItem === MenuItemName.None) {
        return undefined;
    }

    const effectiveMenuItem =
        selectedMenuItem === MenuItemName.Leads
            ? (LEAD_TAB_TO_MENU_ITEM[getLeadTabTypeFromUrl(pathname)] ?? selectedMenuItem)
            : selectedMenuItem;

    for (const node of navNodes) {
        // top-level node rendered directly as a menu item (e.g. reports)
        if (nodeMatchesMenuItem(node, effectiveMenuItem)) {
            return { itemKey: node.key };
        }
        const matchingChild = node.children?.find((child) =>
            nodeMatchesMenuItem(child, effectiveMenuItem),
        );
        if (matchingChild) {
            return { itemKey: matchingChild.key, parentKey: node.key };
        }
    }

    return undefined;
}

/** renders the collapsible main nav menus on the left, the global search, and the persistent "action" menus on the right */
const MainNavMenus = ({
    setMainNavigationRef,
    headerStyleInfo,
    isSmallWidth,
    loginType,
    navNodes,
    quickaddNode,
    actionNavNodes,
    handleShowDrawer,
    handleCloseDrawer,
    menuOverflowOpen,
    noBreakpoints,
    className,
    onNodeActivate,
    openKeys,
    onOpenChange,
    data,
    jobId,
    isTemplateMode,
    builderInfo,
    userInfo,
    appDefaultInfo,
    environmentInfo,
    selectedMenuItem,
    handler,
    setOpenKeys,
}: IMainNavMenusProps) => {
    useEffect(() => {
        document.documentElement.style.setProperty(
            "--main-navigation-background-color",
            `${headerStyleInfo.color}`,
        );
    }, [headerStyleInfo.color]);

    const shouldUseVerticalProps = noBreakpoints !== undefined ? !noBreakpoints : isSmallWidth;
    const [showSearchInput, setShowSearchInput] = useState(false);
    const [quickaddIsExpanded, setQuickaddIsExpanded] = useState(false);

    const isReadQueryEnabled =
        !userInfo.isBuilderImpersonatingOwner &&
        (data?.isChatEnabled ?? false) === true &&
        selectedMenuItem !== MenuItemName.Chat;
    const allowSearch = data.isSearchEnabled && !isTemplateMode;
    const notificationsDropdownIsVisible = openKeys.includes("notifications");
    const whatsNewDropdownIsVisible = openKeys.includes("whatsNew");

    const {
        data: hasUnreadChatData,
        isFetching: isFetchingChatReadData,
        refetch: refetchChatReadData,
    } = useGetChatQuery(handler, appDefaultInfo?.chatMainNavIndicatorPollingRateInSeconds ?? 60, {
        enabled: isReadQueryEnabled,
    });
    const hasUnreadChat = isReadQueryEnabled && hasUnreadChatData?.hasUnread;
    const { trackEvent } = useTracking();

    useEffect(() => {
        (async () => {
            const hasUnreadData = BTLocalStorage.get("bt-boolean-hasUnreadChat");
            if (!isFetchingChatReadData && hasUnreadData === null) {
                await queryClient.invalidateQueries([ChatReadQueryKey]);
            }
        })();
    }, [isFetchingChatReadData, refetchChatReadData]);

    const handleActivate = (
        info: IMenuTabInfo | undefined,
        navItem: NavMenuItemResponse | undefined,
        title: string,
    ) => onNodeActivate(info, navItem, title);

    const notificationMenuItemRef = useRef<HTMLDivElement>(null); // tracks the item we return focus to when the notification panel closes
    const whatsNewMenuItemRef = useRef<HTMLDivElement>(null); // same, for the What's New panel
    const quickaddToggleExpandButtonRef = useRef<HTMLButtonElement>(null); // tracks the expand/collapse button for quickadd menu

    const widthDependentProps = shouldUseVerticalProps
        ? {
              className: "main-navigation-vertical",
              homeButtonPosition: "end" as const,
              menuMode: "inline" as const,
              showNavCaret: true,
              Tag: MainNavVertical,
          }
        : {
              className: "main-navigation-horizontal",
              homeButtonPosition: "start" as const,
              menuMode: "horizontal" as const,
              showNavCaret: false,
              Tag: MainNavHorizontal,
          };

    const { Tag } = widthDependentProps;

    const { pathname } = useLocation();
    const selectedNav = useMemo(
        () => getSelectedNav(navNodes, selectedMenuItem, pathname),
        [navNodes, selectedMenuItem, pathname],
    );

    const renderChild = (child: IMainNavChildNode) => {
        const isQuickAddItem = isChildNodeWithQuickAddLink(child);

        const handleContentClick = (info: IMenuTabInfo, navItem: NavMenuItemResponse) => {
            // Track quick add clicks in local storage
            if (isQuickAddItem) {
                trackQuickAddClick(child.key);
            }
            handleActivate(info, navItem, child.title);
        };

        // In inline mode the child items are rendered, so mark the matched leaf selected. This also
        // covers top-level items rendered directly as a Menu.Item (e.g. reports).
        const isSelected = selectedNav?.itemKey === child.key;

        return (
            // eslint-disable-next-line react/forbid-elements
            <Menu.Item
                key={child.key}
                icon={child.icon}
                className={cn("main-navigation-menu-item", {
                    "ant-menu-item-selected": isSelected,
                })}
                disabled={child.disabled}
            >
                {!child.disabled && (
                    <MainNavLink
                        id={child.key}
                        quickAddLink={isQuickAddItem ? child.quickAddLink : undefined}
                        data-testid={child.testId}
                        info={child.info}
                        data={data}
                        navMenuItem={child.navItem}
                        jobId={jobId}
                        isTemplateMode={isTemplateMode}
                        loginType={loginType}
                        onContentClick={handleContentClick}
                        builderInfo={builderInfo}
                        userInfo={userInfo}
                        appDefaultInfo={appDefaultInfo}
                        environmentInfo={environmentInfo}
                        removeTabIndex={child.key === "reports"}
                    >
                        {child.info?.tag ? (
                            <div className="flex flex-row gap-1 items-center">
                                {child.title}
                                {child.info.tag}
                            </div>
                        ) : (
                            <> {child.title}</>
                        )}
                    </MainNavLink>
                )}
                {child.disabled && (
                    <span
                        id={child.key}
                        data-testid={child.testId}
                        role="menuitem"
                        aria-disabled="true"
                        tabIndex={-1}
                    >
                        {child.title}
                    </span>
                )}
            </Menu.Item>
        );
    };

    const renderNode = (node: IMainNavNode, isActionNode: boolean = false) => {
        if (node.children && node.children.length > 0) {
            // In horizontal mode the child items are lazily rendered inside a popup, so the visible
            // top-bar element is this parent submenu. Mark it selected when the active page belongs to
            // one of its children. (Action-menu categories are not selectable.)
            const isSelected = !isActionNode && selectedNav?.parentKey === node.key;

            return (
                <Menu.SubMenu
                    key={node.key}
                    popupOffset={[-10, 0]}
                    data-testid={node.testId + (isActionNode ? "-action-submenu" : "-submenu")}
                    title={
                        <MainNavTitle
                            testid={node.testId}
                            title={node.icon ? node.icon : node.title}
                            // only show the caret if there is no icon and we are horizontal
                            showNavCaret={widthDependentProps.showNavCaret && !node.icon}
                            closed={!openKeys.includes(node.key)}
                            {...(isActionNode ? { ariaLabel: node.key } : {})}
                        />
                    }
                    className={cn("main-navigation-submenu", {
                        "ant-menu-submenu-selected": isSelected,
                    })}
                >
                    {node.children.flatMap((n) =>
                        n.info?.dividerBefore
                            ? [<Menu.Divider key={`${n.key}-divider`} />, renderChild(n)]
                            : [renderChild(n)],
                    )}
                </Menu.SubMenu>
            );
        }

        return renderChild(node);
    };

    const renderQuickaddNode = (node: IMainNavNode<IMainNavChildNodeWithSortingData>) => {
        if (!node.children || node.children.length === 0) return null;
        const children = node.children;

        const clickedItemsCount = children.filter((child) => child.clickCount > 0).length;
        const hasClickedItems = clickedItemsCount > 0;

        // If no items clicked, show all items. Otherwise show up to MAX_QUICK_ADD_DISPLAY_COUNT clicked items
        const collapsedItemCount = hasClickedItems
            ? Math.min(clickedItemsCount, MAX_QUICK_ADD_DISPLAY_COUNT)
            : children.length;
        // Only show expand button if user has clicked items and there are more items to show
        const showExpand = hasClickedItems && children.length > collapsedItemCount;
        const showAllQuickaddItems = quickaddIsExpanded && showExpand;

        /**
         * By passing focus to the button element within the Menu.Item, we can capture key events before they reach the Menu.Item,
         * allowing us to prevent the default behavior of closing the SubMenu when space/enter is pressed
         */
        const handoffFocus = () => {
            quickaddToggleExpandButtonRef.current?.focus();
        };
        return (
            <Menu.SubMenu
                key={node.key}
                popupOffset={[-10, 0]}
                title={
                    <MainNavTitle
                        testid={node.testId}
                        title={node.icon}
                        showNavCaret={false}
                        closed={!openKeys.includes(node.key)}
                        ariaLabel="Quick add"
                    />
                }
                className="main-navigation-submenu"
            >
                {hasClickedItems && (
                    <div className="quickadd-header-container">
                        <div className="quick-add-header">Frequently Added</div>
                    </div>
                )}

                {quickaddIsExpanded || !hasClickedItems ? (
                    // Show all items when expanded OR when user has no click history
                    <>
                        {/* Frequently used items */}
                        {children.slice(0, collapsedItemCount).map((child) => renderChild(child))}
                        {/* Divider and remaining items when there are clicked items */}
                        {hasClickedItems && children.length > collapsedItemCount && (
                            <>
                                <Menu.Divider key="divider" />
                                {children
                                    .slice(collapsedItemCount)
                                    .map((child) => renderChild(child))}
                            </>
                        )}
                    </>
                ) : (
                    // When collapsed with clicked items: show top items up to MAX_QUICK_ADD_DISPLAY_COUNT
                    children.slice(0, collapsedItemCount).map((child) => renderChild(child))
                )}

                {showExpand && (
                    // eslint-disable-next-line react/forbid-elements
                    <Menu.Item
                        key="quickadd-showmore"
                        className="quickadd-showmore"
                        onFocus={handoffFocus}
                    >
                        <BTButton
                            className="quickadd-showmore-button"
                            ref={quickaddToggleExpandButtonRef}
                            id="expand-collapse-quickadd"
                            data-testid="expand-collapse-quickadd"
                            type="tertiary"
                            icon={
                                showAllQuickaddItems ? <IconCaretSmallUp /> : <IconCaretSmallDown />
                            }
                            aria-label={
                                showAllQuickaddItems ? "Collapse quick add" : "Expand quick add"
                            }
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setQuickaddIsExpanded((v) => !v);
                            }}
                        />
                    </Menu.Item>
                )}
            </Menu.SubMenu>
        );
    };

    // notification menu stuff
    const trackNotificationPanelClose = () => {
        trackEvent({
            // Event added in ado-244064
            // Until then, we will cast to keep TypeScript happy
            event: "PopoverClose" as "PopoverOpen",
            uniqueId: "notificationPanelClose",
        });
    };

    const getWhatsNewMenuItem = () => {
        if (!whatsNewDropdownIsVisible || builderInfo === undefined) return <></>;
        return (
            <div
                className={menuItemClass}
                onClick={(e) => {
                    // Stop the click so antd doesn't close the menu.
                    e.stopPropagation();
                }}
            >
                <WhatsNewPanel
                    builderId={builderInfo.builderId}
                    userId={userInfo.globalUserId}
                    onClose={() => {
                        onOpenChange([]);
                        // Menu.Item takes no ref, so focus the li wrapper the icon sits in.
                        whatsNewMenuItemRef.current?.closest("li")?.focus();
                    }}
                />
            </div>
        );
    };

    const getNotificationMenuItem = () => {
        if (!notificationsDropdownIsVisible) return <></>;
        return (
            <div
                className={menuItemClass}
                onClick={(e) => {
                    // swallow click events so antd doesnt close the menu
                    e.stopPropagation();
                }}
            >
                <NotificationPanel
                    selectedJobId={jobId}
                    onClose={() => {
                        trackNotificationPanelClose();
                        onOpenChange([]);
                        if (notificationMenuItemRef.current) {
                            // try to return focus to the li wrapper of menu item, as Menu.Item does not take a ref
                            const notificationNavMenuItemWrapper =
                                notificationMenuItemRef.current.closest("li");
                            notificationNavMenuItemWrapper?.focus();
                        }
                    }}
                />
            </div>
        );
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (
            (e.key === "ArrowRight" || e.key === "ArrowLeft" || e.key === "Tab") &&
            openKeys.length > 0
        ) {
            e.stopPropagation();
            e.preventDefault();
        }
    };

    const renderMenuContent = () => (
        // eslint-disable-next-line react/forbid-elements
        <Menu
            id="main-navigation-menu"
            data-testid="main-navigation-menu"
            mode={widthDependentProps.menuMode}
            className={widthDependentProps.className}
            triggerSubMenuAction="click"
            overflowedIndicator={null}
            // Highlighting is driven entirely by the current route (via the injected selected
            // classes in renderChild/renderNode), so disable antd's uncontrolled last-click
            // selection — otherwise a stale clicked item and the route-matched item both highlight.
            selectable={false}
            openKeys={openKeys}
            onOpenChange={onOpenChange}
            getPopupContainer={(el) => el.parentElement as HTMLElement}
            disabledOverflow
            onKeyDownCapture={handleKeyDown}
        >
            {navNodes.map((n) => renderNode(n))}
        </Menu>
    );

    const renderActionMenuContent = (setOpenKeys: (keys: string[]) => void) => (
        <div className="flex flex-row">
            <MainNavGlobalSearch
                allowSearch={allowSearch}
                selectedMenuItem={selectedMenuItem}
                data={data}
                isSmallWidth={noBreakpoints !== undefined ? !noBreakpoints : isSmallWidth}
                showSearchInput={showSearchInput}
                setShowSearchInput={setShowSearchInput}
                onFocus={() => setOpenKeys([])} // this will close any open submenus when focusing the search input
                loginType={loginType}
                userInfo={userInfo}
                jobId={jobId}
            />

            {(!showSearchInput || !isSmallWidth) && (
                // eslint-disable-next-line react/forbid-elements
                <Menu
                    id="main-navigation-actions-menu"
                    mode="horizontal"
                    selectable={false}
                    className="main-navigation-action-items-menu"
                    triggerSubMenuAction="click"
                    openKeys={openKeys}
                    onOpenChange={onOpenChange}
                    getPopupContainer={(el) => el.parentElement as HTMLElement}
                    onKeyDownCapture={handleKeyDown}
                    disabledOverflow
                    style={{
                        background: "transparent",
                        borderBottom: "none",
                        whiteSpace: "nowrap",
                    }}
                >
                    {/* quick add links */}
                    {renderQuickaddNode(quickaddNode)}

                    {/* notifications */}
                    {/* eslint-disable-next-line react/forbid-elements */}
                    <Menu.Item
                        aria-label="Notifications"
                        key="notifications"
                        className="main-navigation-submenu"
                        data-testid="notifications-main-nav-button"
                        onClick={() => {
                            if (notificationsDropdownIsVisible) {
                                onOpenChange([]);
                            } else {
                                onOpenChange(["notifications"]);
                            }
                        }}
                    >
                        {/* eslint-disable-next-line react/forbid-elements */}
                        <Dropdown
                            overlayClassName="main-navigation-notification-dropdown"
                            overlay={getNotificationMenuItem()}
                            trigger={["click"]}
                            visible={notificationsDropdownIsVisible}
                            onVisibleChange={(open) =>
                                open ? onOpenChange(["notifications"]) : onOpenChange([])
                            }
                        >
                            <NewNotificationsBadge
                                refetchInternalSeconds={
                                    appDefaultInfo?.unreadNotificationCountCacheTimeout ?? 300
                                }
                            >
                                <IconNotifications innerRef={notificationMenuItemRef} />
                            </NewNotificationsBadge>
                        </Dropdown>
                    </Menu.Item>
                    {/* what's new */}
                    {/* Builders only: clients and subs share this nav, and the API refuses them.
                        Not <WhenBuilderFlag>: antd's Menu hides a Menu.Item that's wrapped. */}
                    {loginType === BTLoginTypes.BUILDER &&
                        builderInfo !== undefined &&
                        builderInfo.flags.whatsNewPanel && (
                            // eslint-disable-next-line react/forbid-elements -- child of a raw antd Menu
                            <Menu.Item
                                aria-label="What's new"
                                key="whatsNew"
                                className="main-navigation-submenu"
                                data-testid="whatsNewMainNavButton"
                                onClick={() => {
                                    if (whatsNewDropdownIsVisible) {
                                        onOpenChange([]);
                                    } else {
                                        onOpenChange(["whatsNew"]);
                                    }
                                }}
                            >
                                {/* eslint-disable-next-line react/forbid-elements -- matches notifications */}
                                <Dropdown
                                    overlayClassName="main-navigation-notification-dropdown"
                                    overlay={getWhatsNewMenuItem()}
                                    trigger={["click"]}
                                    visible={whatsNewDropdownIsVisible}
                                    onVisibleChange={(open) =>
                                        open ? onOpenChange(["whatsNew"]) : onOpenChange([])
                                    }
                                >
                                    <WhatsNewNavBadge
                                        builderId={builderInfo.builderId}
                                        userId={userInfo.globalUserId}
                                        refetchIntervalSeconds={
                                            appDefaultInfo?.whatsNewPollInterval ?? 1800
                                        }
                                    >
                                        <IconMegaphone innerRef={whatsNewMenuItemRef} />
                                    </WhatsNewNavBadge>
                                </Dropdown>
                            </Menu.Item>
                        )}
                    {/* chat */}
                    {data.isChatEnabled && (
                        // eslint-disable-next-line react/forbid-elements
                        <Menu.Item
                            key="chat"
                            className="main-navigation-chat-item"
                            aria-label="Chat"
                        >
                            <BTLink useAutoSPARouting to={routes.chat.getListLink()} tabIndex={-1}>
                                <MainNavBadge dot={hasUnreadChat}>
                                    <IconLiveChat testid="chat-main-nav-button" />
                                </MainNavBadge>
                            </BTLink>
                        </Menu.Item>
                    )}
                    {/* action items */}
                    {actionNavNodes.map((n) => renderNode(n, true))}
                </Menu>
            )}
        </div>
    );
    return (
        <nav
            data-testid="bt-main-navigation"
            ref={setMainNavigationRef}
            className={cn("main-navigation-2", widthDependentProps.className, className, {
                "main-navigation-dark-text": !isDark(headerStyleInfo.color),
                "main-navigation-small-width": isSmallWidth,
                OwnerPreview: userInfo.isBuilderImpersonatingOwner,
            })}
            style={{ backgroundColor: headerStyleInfo.color }}
        >
            {/* Left Menu Items */}
            <Tag
                {...(shouldUseVerticalProps
                    ? { onOpen: handleShowDrawer, loginType: loginType }
                    : { loginType: loginType })}
                backgroundColor={headerStyleInfo.color}
            >
                {widthDependentProps.homeButtonPosition === "start" && (
                    <MainNavHomeButton
                        loginType={loginType}
                        backgroundColor={headerStyleInfo.color}
                    />
                )}
                {!shouldUseVerticalProps && renderMenuContent()}
            </Tag>
            {shouldUseVerticalProps && (
                <MainNavDrawer
                    open={menuOverflowOpen}
                    onClose={handleCloseDrawer}
                    loginType={loginType}
                    backgroundColor={headerStyleInfo.color}
                >
                    {renderMenuContent()}
                </MainNavDrawer>
            )}
            {/* Builder-only: this nav is shared with the owner and sub portals, and the
                Research Preview is a builder-side experience. Gated on the same
                shouldUseVerticalProps the hamburger/drawer use (rather than an independent
                breakpoint) so this and the drawer's copy can never both be visible at once. */}
            {loginType === BTLoginTypes.BUILDER && !shouldUseVerticalProps && (
                <WhenBuilderFlag
                    isEnabled="infoArchOptIn"
                    then={
                        <InfoArchOptInToggle
                            labelClassName={
                                isDark(headerStyleInfo.color) ? "text-gray-0" : "text-gray-100"
                            }
                        />
                    }
                />
            )}
            {/* Global Search & Right Action Items */}

            {renderActionMenuContent(setOpenKeys)}
        </nav>
    );
};

const MainNavigationInternal = (props: IMenuItemsProps) => {
    const {
        setMainNavigationRef,
        headerStyleInfo,
        isSmallWidth,
        data,
        loginType,
        selectedMenuItem,
        jobId,
        generalJobId,
        isTemplateMode,
        builderId,
        userId,
        tracking,
        handler = defaultMainNavigationHandler,
        menuInfo = defaultMenuInfo,
    } = props;

    const appDefaultInfo = useContext(AppDefaultInfoContext);

    const [openKeys, setOpenKeys] = useState<string[]>([]);
    const [menuOverflowOpen, setMenuOverflowOpen] = useState(false);

    const [activeUnfollowNotificationModel, setActiveUnfollowNotificationModel] =
        useState<INotificationUnfollowModel | null>(null);

    const { location, history, match, removeQueryStringParamsReactRouter } = useRouteProps();

    const envInfo = useContext(EnvironmentInfoContext);
    const builderInfo = useRequiredContext(BuilderInfoContext);
    const userInfo = useRequiredContext(UserInfoContext);
    // The nav item is already hidden when the user lacks Selections View, and the config endpoint
    // requires that same permission — fetching would only raise a 403 toast without ever changing
    // what renders
    const canViewSelections = data.projectManagement?.selections?.visible ?? false;
    const templateSelectionsState = useTemplateSelectionsState(
        jobId,
        isTemplateMode,
        builderInfo.flags.selectionsReimagined &&
            // With template support on the item always shows, so the fetch changes nothing
            !builderSupportsTemplateSelectionOptions({
                selectionsReimagined: builderInfo.flags.selectionsReimagined,
                templateSupportForSelectionOptions:
                    builderInfo.flags.templateSupportForSelectionOptions,
                hasLegacySelectionsData: builderInfo.hasLegacySelectionsData,
                legacyBuildersSriTemplateSupport:
                    builderInfo.flags.legacyBuildersSriTemplateSupport,
            }) &&
            canViewSelections,
    );

    useEffect(() => {
        if (menuOverflowOpen && !isSmallWidth) {
            setMenuOverflowOpen(false);
            setOpenKeys([]);
        }
    }, [isSmallWidth, menuOverflowOpen]);

    const handleShowDrawer = () => setMenuOverflowOpen(true);
    const handleCloseDrawer = () => setMenuOverflowOpen(false);
    const onOpenChange = (keys: string[]) => {
        let tempKeys = [];
        // main nav only allows one submenu open at a time, so use the last key in the array
        if (keys.length) {
            const newKey = keys[keys.length - 1];
            // reports and chat are special cases that do not have a submenu
            if (["reports", "chat"].includes(newKey)) {
                tempKeys = [];
                setOpenKeys([]);
            } else {
                tempKeys = [newKey];
                setOpenKeys([newKey]);
                if (newKey === "help") {
                    // reload qualtrics survey when help menu is opened
                    const el = document.querySelector("#giveFeedback > a") as HTMLAnchorElement;
                    // Qualtrics intercept link has not been applied yet, so reload to re-evaluate
                    if (!el) reloadQualtrics();
                }
            }
        } else {
            tempKeys = [];
            setOpenKeys([]);
        }

        /**
         *  Due to an issue with chrome on windows, when opening a submenu the submenu renders offscreen to the right
         *  and causes a horizontal scrollbar to appear.  This is a workaround to disable horizontal scrolling when
         *  a submenu is open. This issue does not appear to affect macOS or iOS.
         */
        document.body.style.overflowX = tempKeys.length > 0 ? "hidden" : "";
    };

    const handleItemClick = useCallback(
        (key: string, isExternalLink?: boolean) => {
            tracking?.trackEvent({
                event: "MenuItemClick",
                element: "Navigation Menu Item",
                uniqueId: key,
                extraInfo: isExternalLink ? { menuType: "External Link" } : undefined,
            });
        },
        [tracking],
    );

    const handleContentClick = useCallback(
        (info: IMenuTabInfo, navMenuItem: NavMenuItemResponse) => {
            if (info.onClick) {
                info.onClick(
                    { userId, builderId, jobId, isTemplateMode, builderInfo },
                    data,
                    { location: location, match: match, history: history },
                    appDefaultInfo,
                    userInfo,
                );
            }
            handleItemClick(
                navMenuItem.title,
                info.onClickType?.(builderInfo, userInfo) === MenuOnClickTypes.External,
            );

            if (menuOverflowOpen) {
                setMenuOverflowOpen(false);
            }
        },
        [
            handleItemClick,
            builderInfo,
            userInfo,
            menuOverflowOpen,
            userId,
            builderId,
            jobId,
            isTemplateMode,
            data,
            location,
            match,
            history,
            appDefaultInfo,
        ],
    );

    const handleDialogClose = useCallback(() => {
        history.replace(match.url);
    }, [match, history]);

    /*
     * NOTE: This triggers a full page reload. Prefer updating specific stale data instead of calling this.
     */
    const handleDialogCloseRefreshPage = useCallback(async () => {
        handleDialogClose();
        window.location.reload();
    }, [handleDialogClose]);

    const legacyModalConfigReloadPage = {
        parentRoute: match.url,
        beforeClose: handleDialogCloseRefreshPage,
    };

    const modalConfigReloadPageQuickAction = {
        parentRoute: match.url + "/QuickAction",
        beforeClose: handleDialogCloseRefreshPage,
    };

    const modalConfigReloadPage = {
        parentRoute: match.url,
        beforeClose: handleDialogCloseRefreshPage,
    };

    const modalConfigNoReloadPage = {
        parentRoute: match.url + "/QuickAction",
        beforeClose: handleDialogClose,
    };

    const ephRemainingDays = GetEphDaysRemaining(envInfo);

    const navNodes = useMemo(
        () =>
            buildNavNodes(
                menuInfo,
                data,
                isTemplateMode,
                loginType,
                MAIN_NAVIGATION_TOP_LEVEL_MENUS,
                jobId,
                builderInfo,
                userInfo,
                templateSelectionsState,
            ),
        [
            menuInfo,
            data,
            isTemplateMode,
            loginType,
            jobId,
            builderInfo,
            userInfo,
            templateSelectionsState,
        ],
    );
    const isGeneralJob = generalJobId !== undefined && generalJobId === jobId;
    const quickAddJobId = isGeneralJob ? 0 : jobId;
    const subMenuProps: Omit<IBTSubMenuItemProps, "itemKey"> = {
        data,
        menuInfo,
        selectedMenuItem: selectedMenuItem!,
        jobId,
        isTemplateMode,
        loginType,
        onContentClick: handleContentClick,
        routeInfo: {
            userId,
            builderId,
            jobId: quickAddJobId,
            isTemplateMode,
            builderInfo,
        },
        mainNavResponse: data,
        builderInfo,
        userInfo,
        appDefaultInfo,
        envInfo,
        onItemClick: handleItemClick,
        templateSelectionsState,
    };

    const quickaddNode = useMemo(
        () =>
            buildQuickaddNode(
                menuInfo,
                data,
                {
                    userId,
                    builderId,
                    jobId: quickAddJobId,
                    isTemplateMode,
                    builderInfo,
                },
                templateSelectionsState,
            ),
        [
            menuInfo,
            data,
            userId,
            builderId,
            quickAddJobId,
            isTemplateMode,
            builderInfo,
            templateSelectionsState,
        ],
    );

    const actionNavNodes = useMemo(
        () =>
            buildNavNodes(
                menuInfo,
                data,
                isTemplateMode,
                loginType,
                MAIN_NAVIGATION_TOP_LEVEL_ACTIONS,
                jobId,
                builderInfo,
                userInfo,
                templateSelectionsState,
            ),
        [
            menuInfo,
            data,
            isTemplateMode,
            loginType,
            jobId,
            builderInfo,
            userInfo,
            templateSelectionsState,
        ],
    );

    // this useEffect is for summary grid action query params "sga"
    useEffect(() => {
        const url = new URL(window.location.href);
        const params = new URLSearchParams(url.search);
        if (params.has("sga")) {
            if (
                params.get("sga") === SummaryGridActionQueryParamTypes.UnfollowNotification &&
                params.has("notificationId") &&
                params.has("builderId")
            ) {
                setActiveUnfollowNotificationModel({
                    builderId: Number(params.get("builderId")),
                    notificationId: Number(params.get("notificationId")),
                });
            }

            removeQueryStringParamsReactRouter(...params.keys());
        }
    }, [history, match.url, removeQueryStringParamsReactRouter]);

    return (
        <>
            {userInfo.isBuilderImpersonatingOwner && (
                <HomeOwnerPreviewBanner
                    impersonatingUserCanEditPermissions={data.impersonatingUserCanEditPermissions}
                    onEditPermissionsClick={
                        // On JCB the permissions that matter live in the JCB client permission
                        // panel, so open that instead of the general drawer.
                        selectedMenuItem === MenuItemName.JobCostingBudget &&
                        builderInfo.flags.jcbNestedCostCategories
                            ? () =>
                                  history.push(routes.jobCostingBudget.getClientPortalDrawerLink())
                            : undefined
                    }
                />
            )}
            {userInfo.isBuilderImpersonatingOwner && data.impersonatingUserCanEditPermissions && (
                <RouteClientPortalSettingsDrawer
                    jobId={jobId}
                    modalConfig={modalConfigNoReloadPage}
                    refreshPage={handleDialogCloseRefreshPage}
                />
            )}
            {envInfo &&
                !envInfo.isPublicEph &&
                !envInfo.isProduction &&
                envInfo.name !== "Production" && (
                    <div style={{ background: envInfo.color }} className="environment-title">
                        {envInfo.name}{" "}
                        {ephRemainingDays !== undefined && (
                            <span>
                                ({ephRemainingDays}{" "}
                                {formattedPlural({
                                    value: ephRemainingDays,
                                    one: "day",
                                    other: "days",
                                })}
                                {" remaining"})
                            </span>
                        )}
                    </div>
                )}
            <MainNavMenus
                setMainNavigationRef={setMainNavigationRef}
                headerStyleInfo={headerStyleInfo}
                isSmallWidth={isSmallWidth}
                loginType={loginType}
                navNodes={navNodes}
                actionNavNodes={actionNavNodes}
                quickaddNode={quickaddNode}
                handleShowDrawer={handleShowDrawer}
                handleCloseDrawer={handleCloseDrawer}
                menuOverflowOpen={menuOverflowOpen}
                noBreakpoints={props.noBreakpoints}
                className={props.className}
                onNodeActivate={(info, navItem, title) => {
                    if (info && navItem) {
                        handleContentClick(info, navItem);
                    } else if (title) {
                        handleItemClick(title);
                    }
                }}
                openKeys={openKeys}
                onOpenChange={onOpenChange}
                data={data}
                jobId={jobId}
                isTemplateMode={isTemplateMode}
                builderInfo={builderInfo}
                userInfo={userInfo}
                appDefaultInfo={appDefaultInfo}
                environmentInfo={envInfo}
                selectedMenuItem={selectedMenuItem}
                subMenuProps={subMenuProps}
                handler={handler}
                setOpenKeys={setOpenKeys}
            />
            <Route render={(routeProps) => <NotificationPanelRoutes {...routeProps} />} />
            <RouteRelative path="/QuickAction">
                {/* Routes for Quick Add Actions */}
                <RouteBidPackage modalConfig={modalConfigReloadPageQuickAction} />
                <RouteBill modalConfig={modalConfigReloadPageQuickAction} />
                <RouteChangeOrderQuickAdd modalConfig={modalConfigReloadPageQuickAction} />
                <RouteContact modalConfig={modalConfigReloadPageQuickAction} />
                <RouteInternalUser modalConfig={modalConfigReloadPageQuickAction} />
                <RouteInternalUserRolesList modalConfig={modalConfigReloadPageQuickAction} />
                <RouteInternalUserRoleDetails modalConfig={modalConfigReloadPageQuickAction} />
                <RouteSelectionOption modalConfig={modalConfigReloadPageQuickAction} />
                <RouteJob
                    parentRoute={match.url + "/QuickAction"}
                    history={history}
                    beforeClose={handleDialogCloseRefreshPage}
                />
                <RouteLead modalConfig={modalConfigReloadPageQuickAction} />
                <RouteLeadActivity modalConfig={modalConfigReloadPageQuickAction} />
                <RouteMessageCompose modalConfig={modalConfigReloadPageQuickAction} />
                <RouteOwnerInvoice modalConfig={modalConfigReloadPageQuickAction} />
                <RoutePurchaseOrder modalConfig={modalConfigReloadPageQuickAction} />
                <RouteRfi modalConfig={modalConfigReloadPageQuickAction} />
                <RouteSchedule modalConfig={modalConfigReloadPageQuickAction} />
                <RouteSelection modalConfig={modalConfigReloadPageQuickAction} />
                <RouteSelectionQuickAdd modalConfig={modalConfigReloadPageQuickAction} />
                <RouteSpecification modalConfig={modalConfigReloadPageQuickAction} />
                <RouteShift modalConfig={modalConfigReloadPageQuickAction} />
                <RouteSubVendor modalConfig={modalConfigReloadPageQuickAction} />
                <RouteTemplate modalConfig={modalConfigReloadPageQuickAction} />
                <RouteTemplateFromTemplate modalConfig={modalConfigReloadPageQuickAction} />
                <RouteToDo modalConfig={modalConfigReloadPageQuickAction} />
                <RouteUploadReceipt modalConfig={modalConfigReloadPageQuickAction} />
                <RouteWarranty modalConfig={modalConfigReloadPageQuickAction} />
                <RouteWarrantyAddQuickAction />
            </RouteRelative>
            <RouteContactUs
                modalConfig={{
                    parentRoute: match.url,
                    beforeClose: handleDialogClose,
                }}
            />
            {/* Id-less self-service route: opens the current user's Internal User Details
                modal (id resolved from context, never in the URL). */}
            <RouteRelative path={routes.internalUsers.myUserDetails}>
                <RouteMyUserDetails />
            </RouteRelative>
            <RouteSubSetup modalConfig={legacyModalConfigReloadPage} />
            <RouteOwnerSetup modalConfig={legacyModalConfigReloadPage} />
            <RouteSubscriptionManager modalConfig={modalConfigReloadPage} />
            <RouteRelative path={routes.accounting.quickBooksSyncSchedule}>
                <QuickBooksSyncSchedule modalConfig={modalConfigReloadPage} />
            </RouteRelative>
            <ShowOnPortal
                builder
                subs
                render={() => (
                    <div className="ProcessStatusPopupContainer">
                        <ClientObservableBackgroundJobStatusPopup
                            history={history}
                            location={location}
                            match={match}
                            jobId={jobId}
                        />
                    </div>
                )}
            />
            {activeUnfollowNotificationModel && (
                <NotificationUnfollow
                    builderId={activeUnfollowNotificationModel.builderId}
                    history={history}
                    location={location}
                    match={match}
                    notificationId={activeUnfollowNotificationModel.notificationId}
                    modalConfig={{
                        beforeClose: () => {
                            setActiveUnfollowNotificationModel(null);
                        },
                        parentRoute: match.url,
                    }}
                />
            )}
        </>
    );
};

export const MainNavigation = track({ component: "Main Navigation" })(({
    builderId,
    userId,
    jobId,
    selectedMenuItem,
    isTemplateMode,
    loginType,
    riskInsurance,
    handler = defaultMainNavigationHandler,
    menuInfo = defaultMenuInfo,
    tracking,
    ...props
}: IMainNavigationProps) => {
    const [data, setData] = useState<MainNavigationResponse | null>(null);
    const [isSmallWidth, setIsSmallWidth] = useState(false);
    const [navHeight, setNavHeight] = useState(MainNavigationStickyHeaderHeight);
    const [mainNavigationPopoverContainerRef, setMainNavigationRef] = useResizeObserver(() => {
        if (mainNavigationPopoverContainerRef.current) {
            setNavHeight(mainNavigationPopoverContainerRef.current.clientHeight);

            if (
                mainNavigationPopoverContainerRef.current.clientWidth <=
                    MAIN_NAVIGATION_BREAKPOINT &&
                !isSmallWidth
            ) {
                setIsSmallWidth(true);
            } else if (
                mainNavigationPopoverContainerRef.current.clientWidth >
                    MAIN_NAVIGATION_BREAKPOINT &&
                isSmallWidth
            ) {
                setIsSmallWidth(false);
            }
        }
    });

    const userInfo = useContext(UserInfoContext);

    const elNavigationHeight = mainNavigationPopoverContainerRef.current?.clientHeight;

    // Set sticky context of main nav height as a base with state variable so it can be dynamically updated
    useStickyHeight(navHeight, "top", 0);

    const { history } = useRouteProps();

    // The client portal menu depends on the selected job (e.g. Estimate only shows when that job has
    // a visible proposal), and the server session can hold a different job than the one on screen.
    // Callers may pass the portal type (OWNER) or the user's login type (CONTACT) for a client.
    const menuJobId =
        loginType === BTLoginTypes.OWNER || loginType === BTLoginTypes.CONTACT ? jobId : undefined;

    const loadData = useCallback(async () => {
        try {
            // Fire off the API call to get the fresh one
            const resp = await handler.get(menuJobId);
            setData(resp);

            // Store the API response in local storage
            // eslint-disable-next-line no-restricted-globals
            localStorage.setItem(
                LocalStorageKeys.MainNavigationData,
                JSON.stringify(resp.toServiceObject()),
            );
        } catch (e) {
            showAPIErrorMessage(e);
        }
    }, [handler, menuJobId]);

    useEffect(() => {
        // clear query data once they enter the chat page
        if (selectedMenuItem === MenuItemName.Chat) {
            queryClient.setQueryData([ChatReadQueryKey], {
                hasUnread: false,
                lastCheckedDateUtc: moment.utc(),
            });
            BTLocalStorage.set("bt-boolean-hasUnreadChat", false);
        }
    }, [selectedMenuItem]);

    useEffect(() => {
        // Observe nav height and adjust css variable and sticky context state usage
        // this is groundwork for a more responsive ui
        if (elNavigationHeight) {
            // If we have grabbed the height of the navigation update the css var and the nav height state
            // so that sticky context is updated as well
            document.documentElement.style.setProperty(
                "--main-navigation-height",
                `${elNavigationHeight}px`,
            );
            setNavHeight(elNavigationHeight);
        }
    }, [elNavigationHeight]);

    useEffect(() => {
        if (elNavigationHeight && data?.impersonatingUserCanEditPermissions) {
            document.documentElement.style.setProperty(
                "--main-nav-owner-preview-alert-height",
                `${elNavigationHeight * 1.3}px`,
            );
        } else {
            document.documentElement.style.setProperty(
                "--main-nav-owner-preview-alert-height",
                "0px",
            );
        }
    }, [data, elNavigationHeight]);

    useEffect(() => {
        (async () => {
            try {
                // Load the main menu we have in local storage
                // eslint-disable-next-line no-restricted-globals
                const mainMenuString = localStorage.getItem(LocalStorageKeys.MainNavigationData);
                if (mainMenuString) {
                    const data = new MainNavigationResponse(JSON.parse(mainMenuString));
                    if (data.builderId === builderId) {
                        // Skip cache if the builderId has changed
                        setData(data);
                    }
                }
            } catch (e) {
                // Failed to deserialize main navigation cache
            }
            if (riskInsurance) {
                history.replace(
                    jobDetailRoute.link({
                        jobId: riskInsurance?.jobId,
                        defaultTab: JobDetailTab.BRI,
                    }),
                );
            }

            await loadData();
        })();
    }, [builderId, loadData, history, riskInsurance, userInfo?.permissions, userInfo?.roleInfo]);

    if (!data) {
        return null;
    }

    return (
        <MainNavigationInternal
            setMainNavigationRef={setMainNavigationRef}
            headerStyleInfo={data.headerStyleInfo}
            data={data}
            isSmallWidth={isSmallWidth}
            builderId={builderId}
            jobId={jobId}
            userId={userId}
            isTemplateMode={isTemplateMode}
            loginType={loginType}
            handler={handler}
            menuInfo={menuInfo}
            selectedMenuItem={selectedMenuItem}
            {...props}
        />
    );
});
const MainNavGlobalSearch = (props: {
    allowSearch: boolean;
    selectedMenuItem?: MenuItemName;
    data: MainNavigationResponse;
    isSmallWidth: boolean;
    showSearchInput: boolean;
    setShowSearchInput: (show: boolean) => void;
    onFocus: () => void;
    loginType: BTLoginTypes;
    userInfo: UserInfo;
    jobId: number;
}) => {
    const {
        allowSearch,
        selectedMenuItem,
        data,
        isSmallWidth,
        showSearchInput,
        setShowSearchInput,
        onFocus,
        loginType,
        userInfo,
        jobId,
    } = props;

    const builderInfo = useContext(BuilderInfoContext);

    useEffect(() => {
        if (isSmallWidth) {
            setShowSearchInput(false);
        }
    }, [setShowSearchInput, isSmallWidth]);

    const legacyGlobalSearch = (
        <GlobalSearch
            searchRequestedPage={selectedMenuItem}
            builderIdUserIdLookup={data.builderIdUserIdLookup}
            handleHideSearchInput={() => setShowSearchInput(false)}
            onFocus={onFocus}
            builderInfo={builderInfo}
        />
    );

    /**
     * The icon that opens the nav search input below `MAIN_NAVIGATION_BREAKPOINT`,
     * and the X that closes it again. The only controls that set `showSearchInput`,
     * so every branch that renders the input must include them.
     */
    const smallWidthSearchToggle = (
        <>
            {allowSearch && isSmallWidth && !showSearchInput && (
                <BTButton
                    className="main-navigation-button"
                    data-testid="search-main-nav-button"
                    aria-label="Search"
                    onClick={() => setShowSearchInput(true)}
                    icon={<IconMagnifyingGlass />}
                />
            )}
            {isSmallWidth && showSearchInput && (
                <BTButton
                    className="main-navigation-button"
                    data-testid="search-close-main-nav-button"
                    aria-label="Close search"
                    onClick={() => setShowSearchInput(false)}
                    icon={<IconX />}
                />
            )}
        </>
    );

    const legacySearch = (
        <>
            {allowSearch && (!isSmallWidth || showSearchInput) && legacyGlobalSearch}
            {smallWidthSearchToggle}
        </>
    );

    // The dialog is a modal opened by one icon, so it needs no input bar or
    // small-width toggle.
    return (
        <div className="flex flex-row main-navigation-global-search">
            {loginType === BTLoginTypes.BUILDER && allowSearch ? (
                <WhenBuilderFlag
                    isEnabled="aiPoweredSearch"
                    then={
                        <SearchDialogTrigger
                            builderInfo={builderInfo ?? undefined}
                            userInfo={userInfo}
                            selectedJobId={jobId}
                        />
                    }
                    otherwise={legacySearch}
                />
            ) : (
                legacySearch
            )}
        </div>
    );
};
